import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { StreamingCommand } from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type { MultiFormatContentType } from '@atlassian/editor-plugin-ai-streaming-orchestrator/types';

import { unescapeJsonString } from './unescape-json-string';

class ToolCall {
	initialized: boolean = false;
	name: string = '';
	nameKeyIdx: number | null = null;
	arguments: {
		anchorLocalId: string | null;
		attrs: Record<string, unknown> | null;
		contentType: string | null;
		localId: string | null;
		localIds: string[] | null;
		noStream: boolean;
		relationship: 'before' | 'after' | null;
		trueValue: string;
		value: string;
		valueStartIdx: number | null;
		wrapperAttrs: Record<string, unknown> | null;
		wrapperType: string | null;
	} = {
		anchorLocalId: null,
		localId: null,
		contentType: null,
		localIds: null,
		noStream: false,
		relationship: null,
		trueValue: '',
		value: '',
		valueStartIdx: null,
		wrapperAttrs: null,
		wrapperType: null,
		attrs: null,
	};
}

// KEYS
const NAME = '"name"';
const LOCAL_ID = '"localId"';
const LOCAL_IDS = '"localIds"';
const ANCHOR_LOCAL_ID = '"anchorLocalId"';
const RELATIONSHIP = '"relationship"';
const CONTENT_TYPE = '"contentType"';
const VALUE = '"value"';
const WRAPPER_ATTRS = '"wrapperAttrs"';
const WRAPPER_TYPE = '"wrapperType"';
const ATTRIBUTES_TYPE = '"attrs"';

const VALID_CONTENT_TYPES = new Set<string>(['text/markdown', 'text/html', 'application/adf+json']);

// ACTIONS
const REPLACE_DOC = 'replaceDoc';
const REPLACE = 'replaceNode';
const INSERT_BEFORE = 'insertNodeBefore';
const INSERT_AFTER = 'insertNodeAfter';
const APPEND = 'appendNode';
const APPEND_TO_END = 'appendNodeToEnd';
const REMOVE = 'removeNode';
const REMOVE_NODES = 'removeNodes';
const MOVE = 'moveNode';
const WRAP = 'wrap';
const WRAP_NODE = 'wrapNode';
const SET_ATTRIBUTES = 'setAttrs';

type StreamActionType = StreamingCommand['type'];

const actionsRequiringValue = new Set([
	REPLACE_DOC,
	REPLACE,
	INSERT_BEFORE,
	INSERT_AFTER,
	APPEND,
	APPEND_TO_END,
]);
export function isStreamable(actionName: string): boolean {
	return actionsRequiringValue.has(actionName);
}

const actionsRequiringLocalId = new Set([
	REPLACE,
	INSERT_BEFORE,
	INSERT_AFTER,
	REMOVE,
	MOVE,
	WRAP,
	WRAP_NODE,
	SET_ATTRIBUTES,
]);
function requiresLocalId(actionName: string): boolean {
	return actionsRequiringLocalId.has(actionName);
}

function isMoveAction(actionName: string): boolean {
	return actionName === MOVE;
}

function isWrapAction(actionName: string): boolean {
	return actionName === WRAP || actionName === WRAP_NODE;
}

function isUpdateAction(actionName: string): boolean {
	return actionName === SET_ATTRIBUTES;
}

function isRemoveNodesAction(actionName: string): boolean {
	return actionName === REMOVE_NODES;
}

export class InitAction {
	readonly name: StreamActionType;
	readonly localId: string | null;
	readonly contentType: MultiFormatContentType | null;
	readonly localIds: string[] | null;
	readonly wrapperType: string | null;
	readonly wrapperAttrs: Record<string, unknown> | null;
	readonly attrs: Record<string, unknown> | null;
	readonly anchorLocalId: string | null;
	readonly relationship: 'before' | 'after' | null;
	readonly noStream: boolean;

	constructor(
		name: StreamActionType,
		localId: string | null,
		contentType: MultiFormatContentType | null,
		wrapperType: string | null,
		wrapperAttrs: Record<string, unknown> | null,
		attrs: Record<string, unknown> | null,
		anchorLocalId: string | null = null,
		relationship: 'before' | 'after' | null = null,
		localIds: string[] | null = null,
		noStream: boolean = false,
	) {
		this.name = name;
		this.localId = localId;
		this.contentType = contentType;
		this.localIds = localIds;
		this.wrapperType = wrapperType;
		this.wrapperAttrs = wrapperAttrs;
		this.attrs = attrs;
		this.anchorLocalId = anchorLocalId;
		this.relationship = relationship;
		this.noStream = noStream;
	}
}

export class StreamAction {
	readonly partialValue: string;
	readonly rawPartialValue: string;

	constructor(rawPartialValue: string) {
		this.rawPartialValue = rawPartialValue;
		this.partialValue = unescapeJsonString(rawPartialValue);
	}
}

export class FinishAction {
	readonly isFinished: true;

	constructor() {
		this.isFinished = true;
	}
}

export type ParseResult = InitAction | StreamAction | FinishAction;

function findValue(input: string, key: string): { keyIndex: number; value: string } | null {
	const keyIndex = input.indexOf(key);

	if (keyIndex === -1) {
		return null;
	} else {
		let i = keyIndex + key.length;
		let value = '';

		while (i < input.length) {
			const char = input[i];
			const prevChar = i > 0 ? input[i - 1] : undefined;

			if ((char === ':' || char === ' ' || char === '"') && value.length === 0) {
				i++;
			} else if (char === '"' && prevChar !== '\\') {
				return { value, keyIndex };
			} else {
				value += char;
				i++;
			}
		}
	}

	return null;
}

/**
 * Finds a JSON array value for the given key in the input string.
 * Returns the full array as a string (e.g., '["id-1","id-2"]') along with
 * the index of the key, or null if the key is not found or the array is incomplete.
 */
function findArrayValue(input: string, key: string): { keyIndex: number; value: string } | null {
	const keyIndex = input.indexOf(key);

	if (keyIndex === -1) {
		return null;
	}

	// Find the '[' after the key
	let i = keyIndex + key.length;
	while (i < input.length && input[i] !== '[') {
		i++;
	}

	if (i >= input.length) {
		return null;
	}

	// Track bracket depth to find the matching ']'
	let depth = 0;
	let value = '';
	while (i < input.length) {
		const char = input[i];
		if (char === '[') {
			depth++;
		} else if (char === ']') {
			depth--;
		}
		value += char;
		i++;
		if (depth === 0) {
			return { keyIndex, value };
		}
	}

	// Array not yet complete (still streaming)
	return null;
}

function findAdfStartIdx(searchSpace: string): number | null {
	const key = VALUE;
	const keyIndex = searchSpace.indexOf(VALUE);

	if (keyIndex === -1) {
		return null;
	} else {
		let i = keyIndex + key.length;

		while (i < searchSpace.length) {
			const char = searchSpace[i];
			if (char === '"') {
				return i + 1;
			}
			i++;
		}
	}

	return null;
}

function findAdfEndIdx(
	searchSpace: string,
	startIdx: number,
): {
	endIndex: number | null;
	partial: string;
} {
	let partial = '';

	for (let i = startIdx; i < searchSpace.length; i++) {
		const char = searchSpace[i];
		const prevChar = i > 0 ? searchSpace[i - 1] : undefined;

		if (char === '"' && prevChar !== '\\') {
			return { partial, endIndex: i };
		} else {
			partial += char;
		}
	}

	return { partial, endIndex: null };
}

export class PartialStreamParser {
	private toolCalls: ToolCall[] = [];
	private accumulated: string = '';

	reset(): void {
		this.toolCalls = [];
		this.accumulated = '';
	}

	parse(input: string): ParseResult[] {
		this.accumulated += input;
		const results: ParseResult[] = [];

		while (true as boolean) {
			let toolCall = this.toolCalls.at(-1);

			if (!toolCall) {
				toolCall = new ToolCall();
				this.toolCalls.push(toolCall);
			}

			/**
			 * Step 1:
			 * Populate the name/type of the command (e.g., appendNode).
			 */
			if (!toolCall.name) {
				// Start searching from the previous tool call, offset
				// by 1 so we don't find the same one again.
				const previousToolCall = this.toolCalls.at(-2);
				const startIdx = (previousToolCall?.nameKeyIdx ?? -1) + 1;
				const result = findValue(this.accumulated.slice(startIdx), NAME);
				if (result) {
					toolCall.name = result.value;
					toolCall.nameKeyIdx = startIdx + result.keyIndex;
				} else {
					return results;
				}
			}

			const searchStartIdx = toolCall.nameKeyIdx;
			if (searchStartIdx === null) {
				return results;
			}
			// TODO: EDITOR-123 - Can we guarantee that the other keys will come after `name`?
			const searchSpace = this.accumulated.slice(searchStartIdx);

			/**
			 * Step 1.5:
			 * For replaceDoc, enforce that contentType is 'text/html' or
			 * 'application/adf+json'. Any other contentType is rejected at the
			 * parser level — no InitAction is emitted. We wait until the
			 * contentType key appears before deciding.
			 */
			if (
				toolCall.name === REPLACE_DOC &&
				expValEquals('platform_editor_ai_replace_doc', 'isEnabled', true)
			) {
				if (!toolCall.arguments.contentType) {
					const result = findValue(searchSpace, CONTENT_TYPE);
					if (result) {
						if (result.value !== 'text/html' && result.value !== 'application/adf+json') {
							// Unsupported contentType for replaceDoc — skip this tool call entirely.
							results.push(new FinishAction());
							this.toolCalls.push(new ToolCall());
							continue;
						}
						toolCall.arguments.contentType = result.value;
					} else if (searchSpace.indexOf(VALUE) !== -1) {
						// "value" key is present but no contentType — reject (missing required field).
						results.push(new FinishAction());
						this.toolCalls.push(new ToolCall());
						continue;
					} else {
						// Neither contentType nor value have appeared yet — wait for more data.
						return results;
					}
				}
			} else if (
				toolCall.name === REPLACE_DOC &&
				!expValEquals('platform_editor_ai_replace_doc', 'isEnabled', true)
			) {
				// Experiment is off — silently drop the replaceDoc tool call.
				results.push(new FinishAction());
				this.toolCalls.push(new ToolCall());
				continue;
			}

			/**
			 * Step 2:
			 * Populate the local ID if it's required, or localIds for removeNodes.
			 */
			if (isRemoveNodesAction(toolCall.name) && !toolCall.arguments.localIds) {
				const result = findArrayValue(searchSpace, LOCAL_IDS);

				if (result) {
					// eslint-disable-next-line require-unicode-regexp, @atlassian/perf-linting/no-expensive-split-replace -- Ignored via go/ees017 (to be fixed)
					toolCall.arguments.localIds = JSON.parse(result.value.replace(/\\/g, ''));
				} else {
					return results;
				}
			} else if (requiresLocalId(toolCall.name) && !toolCall.arguments.localId) {
				const result = findValue(searchSpace, LOCAL_ID);

				if (result) {
					toolCall.arguments.localId = result.value;
				} else {
					return results;
				}
			}

			/**
			 * Step 2.1:
			 * Populate moveNode-specific parameters.
			 */
			if (isMoveAction(toolCall.name)) {
				if (!toolCall.arguments.anchorLocalId) {
					const result = findValue(searchSpace, ANCHOR_LOCAL_ID);
					if (result) {
						toolCall.arguments.anchorLocalId = result.value;
					} else {
						return results;
					}
				}

				if (!toolCall.arguments.relationship) {
					const result = findValue(searchSpace, RELATIONSHIP);
					if (result && (result.value === 'before' || result.value === 'after')) {
						toolCall.arguments.relationship = result.value;
					} else {
						return results;
					}
				}
			}

			/**
			 * Step 2.2:
			 * Populate the contentType if present (multi-format streaming).
			 * When the flag is on we must wait until either contentType is fully
			 * parsed or the "value" key appears (proving contentType is absent)
			 * before initializing the tool call — otherwise a partial chunk could
			 * cause us to emit InitAction with contentType: null prematurely.
			 */
			if (
				isStreamable(toolCall.name) &&
				!toolCall.arguments.contentType &&
				(expValEquals('platform_editor_ai_multi_format_streaming', 'isEnabled', true) ||
					expValEquals('platform_editor_use_html_plus_parser', 'isEnabled', true))
			) {
				const result = findValue(searchSpace, CONTENT_TYPE);
				if (result && VALID_CONTENT_TYPES.has(result.value)) {
					toolCall.arguments.contentType = result.value;
				} else if (searchSpace.indexOf(VALUE) === -1) {
					return results;
				}
			}

			/**
			 * Step 3:
			 * Populate the additional wrap parameters if this is a wrap action.
			 */
			if (isWrapAction(toolCall.name)) {
				if (!toolCall.arguments.wrapperType) {
					const result = findValue(searchSpace, WRAPPER_TYPE);

					if (result) {
						toolCall.arguments.wrapperType = result.value;
					} else {
						return results;
					}
				}

				if (!toolCall.arguments.wrapperAttrs) {
					const result = findValue(searchSpace, WRAPPER_ATTRS);

					if (result) {
						// eslint-disable-next-line require-unicode-regexp, @atlassian/perf-linting/no-expensive-split-replace -- Ignored via go/ees017 (to be fixed)
						toolCall.arguments.wrapperAttrs = JSON.parse(result.value.replace(/\\/g, ''));
					} else {
						return results;
					}
				}
			}

			/**
			 * Step 3.1:
			 * Populate the additional update parameters for update attribute actions
			 */
			if (isUpdateAction(toolCall.name)) {
				if (!toolCall.arguments.attrs) {
					const result = findValue(searchSpace, ATTRIBUTES_TYPE);

					if (result) {
						// eslint-disable-next-line require-unicode-regexp, @atlassian/perf-linting/no-expensive-split-replace -- Ignored via go/ees017 (to be fixed)
						toolCall.arguments.attrs = JSON.parse(result.value.replace(/\\/g, ''));
					} else {
						return results;
					}
				}
			}

			/**
			 * Step 3.2:
			 * Check for noStream flag to skip streaming
			 */
			if (!toolCall.arguments.noStream) {
				if (searchSpace.includes('"noStream":true') || searchSpace.includes('"noStream": true')) {
					toolCall.arguments.noStream = true;
				}
			}

			/**
			 * Step 4:
			 * At this point, all the information required for initializing
			 * the tool call has been gathered, so push an InitAction and mark
			 * the tool call as initialized.
			 */
			if (!toolCall.initialized) {
				results.push(
					new InitAction(
						toolCall.name as StreamActionType,
						// May be null
						toolCall.arguments.localId,
						// May be null
						(toolCall.arguments.contentType as MultiFormatContentType) ?? null,
						// May be null
						toolCall.arguments.wrapperType,
						// May be null
						toolCall.arguments.wrapperAttrs,
						// May be null
						toolCall.arguments.attrs,
						toolCall.arguments.anchorLocalId,
						toolCall.arguments.relationship,
						// May be null
						toolCall.arguments.localIds,
						toolCall.arguments.noStream,
					),
				);
				toolCall.initialized = true;
			}

			/**
			 * Step 5:
			 * If this tool call isn't streamable (e.g., removeNode), then
			 * we can finalize it and move on to the next tool call.
			 */
			if (!isStreamable(toolCall.name)) {
				// If this is an action we don't need an ADF value for (e.g., remove), then
				// we're done and can move on to the next tool call.
				results.push(new FinishAction());
				this.toolCalls.push(new ToolCall());
				continue;
			}

			/**
			 * Step 6:
			 * Find the starting index of the ADF value.
			 * Example:
			 * "value": "{\"content..."
			 *           ^
			 *           |
			 *           +-- This is the start of the ADF value
			 */
			if (!toolCall.arguments.valueStartIdx) {
				const adfStartIdx = findAdfStartIdx(searchSpace);

				if (adfStartIdx !== null) {
					toolCall.arguments.valueStartIdx = searchStartIdx + adfStartIdx;
				} else {
					return results;
				}
			}

			/**
			 * Step 7:
			 * Start looking for the end of the ADF value. If partial ADF has been
			 * found, we push a StreamAction with the new content. If the end index
			 * has been found, we push a FinishAction and move to the next tool call.
			 */

			// This will push the trailing slashes to the next chunk
			// eslint-disable-next-line require-unicode-regexp, @atlassian/perf-linting/no-expensive-split-replace -- Ignored via go/ees017 (to be fixed)
			const valueEndIdx = this.accumulated.replace(/\\+$/g, '').length;

			const valueEnd = findAdfEndIdx(
				this.accumulated.slice(toolCall.arguments.valueStartIdx, valueEndIdx),
				toolCall.arguments.value.length,
			);

			if (valueEnd.partial.length > 0) {
				toolCall.arguments.value += valueEnd.partial;
				results.push(new StreamAction(valueEnd.partial));
			}

			if (valueEnd.endIndex === null) {
				// We haven't reached the end of the ADF yet
				return results;
			} else {
				// Move to the next tool call
				results.push(new FinishAction());
				this.toolCalls.push(new ToolCall());
			}
		}

		return results;
	}
}
